"use client";
import { Button, Typography } from "@cappelendamm/design-system/react";
import { RefObject, useEffect } from "react";
import { PiCircleNotch, PiX } from "react-icons/pi";
import { CartItems } from "./CartItems";
import Form from "next/form";
import { useCart } from "./CartContext";
import { getCartFreightPrice, getCartNumberOfItems } from "./selectors";
import { usePathname } from "next/navigation";
import MastercardIcon from "@/assets/payment-2/Mastercard.svg";
import VisaIcon from "@/assets/payment-2/Visa.svg";
import FakturaIcon from "@/assets/payment-2/Faktura.svg";
import VippsIcon from "@/assets/payment-2/Vipps.svg";
import { formatPrice } from "@cappelendamm/e-commerce-services";

export type CartProps = {
  ref: RefObject<HTMLDialogElement | null>;
  vippsInNetsForUnitSale: boolean;
};

export function Cart({ ref, vippsInNetsForUnitSale }: CartProps) {
  const { closeCart, cart, cartOpen, isPending } = useCart();
  const pathname = usePathname();
  const numberOfItems = getCartNumberOfItems(cart);
  const freightPrice = getCartFreightPrice(cart) ?? 0;

  useEffect(() => {
    closeCart();
  }, [pathname, closeCart]);

  return (
    <dialog
      className="fixed grid max-h-full min-h-full min-w-full translate-x-[100dvw] grid-rows-[auto_1fr_auto_auto] place-items-center gap-4 p-4 transition-all duration-500 open:right-0 open:translate-x-[calc(100dvw-100%)] sm:min-w-fit starting:open:translate-x-full"
      ref={ref}
      onClick={({ clientX, clientY }) => {
        if (ref.current && cartOpen) {
          const rect = ref.current.getBoundingClientRect();
          const isInDialog =
            rect.top <= clientY &&
            clientY <= rect.top + rect.height &&
            rect.left <= clientX &&
            clientX <= rect.left + rect.width;
          if (!isInDialog) closeCart();
        }
      }}
    >
      <Typography
        as="h2"
        variant="title-md"
        className="order-first font-normal"
      >
        Handlekurv
      </Typography>
      {numberOfItems > 0 ? (
        <Form
          action="/utsjekk"
          className="order-4 col-span-full grid w-full gap-4 text-center"
        >
          <Typography as="h2" variant="title-lg">
            {`Å betale ${formatPrice(cart ? cart.total.gross - freightPrice : null)}`}
            <span className="sr-only">Frakt beregnes i kassen</span>
          </Typography>
          <div aria-hidden="true">Frakt beregnes i kassen</div>
          <Button
            className="w-full"
            type="submit"
            disabled={isPending}
            autoFocus
          >
            {isPending && Boolean(cart) ? (
              <PiCircleNotch className="animate-spin" aria-label="Laster" />
            ) : (
              "Gå til kassen"
            )}
          </Button>
          <div className="flex justify-center gap-2" aria-hidden="true">
            <div className="rounded border px-[3.5px]">
              <MastercardIcon alt="Mastercard ikon" height={32} />
            </div>
            <div className="flex items-center rounded border">
              <VisaIcon alt="Visa ikon" height={32} />
            </div>
            <div className="flex items-center rounded border">
              <FakturaIcon alt="Faktura ikon" height={32} />
            </div>
            {vippsInNetsForUnitSale && (
              <div className="flex items-center rounded border">
                <VippsIcon alt="Vipps ikon" height={32} />
              </div>
            )}
          </div>
        </Form>
      ) : (
        <Button
          type="button"
          onClick={closeCart}
          className="col-span-full w-full"
          variant="secondary"
        >
          Lukk handlekurven
        </Button>
      )}
      <CartItems className="order-2 col-span-full h-fit w-full self-start" />
      <hr className="order-3 col-span-full -mx-4 w-[calc(100%+(--spacing(4))*2)] border-stroke-default" />
      <button
        type="button"
        className="absolute top-4 right-4 order-last"
        onClick={closeCart}
      >
        <PiX size={24} />
        <span className="sr-only">Lukk handlekurven</span>
      </button>
    </dialog>
  );
}
